import { inject, Injectable, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { NativeBiometric, BiometryType } from '@capgo/capacitor-native-biometric';

import type { BiometricCredentials } from '@core/auth/models/biometric.models';
import { LocalStorageService } from '@core/services/local-storage.service';

const SERVER = 'net.dhig.digital.customer.portal';
const ENABLED_KEY = 'biometric-enabled';
const DECLINED_KEY = 'biometric-declined';
const PROMPT_COUNT_KEY = 'biometric-prompt-count';
const PROMPT_LIMIT = 3;

@Injectable({
  providedIn: 'root'
})
export class BiometricService {
  private localStorage = inject(LocalStorageService);

  private enabled = signal<boolean>(false);
  private declined = signal<boolean>(false);
  private biometryType = signal<BiometryType>(BiometryType.NONE);
  private promptCount = signal<number>(0);

  constructor() {
    this.initializeEnabled();
    this.initializeDeclined();
    this.initializeBiometryType();
    this.initializePromptCount();
  }

  isEnabled(): boolean {
    return this.enabled();
  }

  hasDeclined(): boolean {
    return this.declined();
  }

  hasReachedPromptLimit(): boolean {
    return this.promptCount() >= PROMPT_LIMIT;
  }

  async isAvailable(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) return false;

    try {
      const result = await NativeBiometric.isAvailable({ useFallback: false });
      this.biometryType.set(result.biometryType);
      return result.isAvailable;
    } catch {
      return false;
    }
  }

  async enable(email: string, password: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    await NativeBiometric.setCredentials({ username: email, password, server: SERVER });
    this.enabled.set(true);
    this.declined.set(false);
    this.promptCount.set(0);
    await this.localStorage.write(ENABLED_KEY, true);
    await this.localStorage.remove(DECLINED_KEY);
    await this.localStorage.remove(PROMPT_COUNT_KEY);
  }

  async recordPromptDismissal(): Promise<void> {
    const newCount = this.promptCount() + 1;
    this.promptCount.set(newCount);
    await this.localStorage.write(PROMPT_COUNT_KEY, newCount);

    if (newCount >= PROMPT_LIMIT) {
      await this.disable();
    }
  }

  async disable(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    try {
      await NativeBiometric.deleteCredentials({ server: SERVER });
    } catch {
      // Credentials may not exist after a fresh install
    }

    this.enabled.set(false);
    this.declined.set(true);
    await this.localStorage.remove(ENABLED_KEY);
    await this.localStorage.write(DECLINED_KEY, true);
  }

  async resetState(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      try {
        await NativeBiometric.deleteCredentials({ server: SERVER });
      } catch {
        // Credentials may not exist
      }
    }

    this.enabled.set(false);
    this.declined.set(false);
    this.promptCount.set(0);
    await this.localStorage.remove(ENABLED_KEY);
    await this.localStorage.remove(DECLINED_KEY);
    await this.localStorage.remove(PROMPT_COUNT_KEY);
  }

  async verifyAndGetCredentials(): Promise<BiometricCredentials | null> {
    if (!Capacitor.isNativePlatform()) return null;

    try {
      await NativeBiometric.verifyIdentity();
      const credentials = await NativeBiometric.getCredentials({ server: SERVER });
      return credentials;
    } catch {
      return null;
    }
  }

  getBiometryLabel(): string {
    switch (this.biometryType()) {
      case BiometryType.FACE_ID:
        return 'biometric.signInFaceId';
      case BiometryType.TOUCH_ID:
        return 'biometric.signInTouchId';
      case BiometryType.FINGERPRINT:
        return 'biometric.signInFingerprint';
      case BiometryType.FACE_AUTHENTICATION:
        return 'biometric.signInFaceAuthentication';
      case BiometryType.IRIS_AUTHENTICATION:
        return 'biometric.signInIrisAuthentication';
      default:
        return 'biometric.signInBiometric';
    }
  }

  getBiometryIcon(): string {
    switch (this.biometryType()) {
      case BiometryType.FACE_ID:
      case BiometryType.FACE_AUTHENTICATION:
        return 'scan-outline';
      case BiometryType.IRIS_AUTHENTICATION:
        return 'eye-outline';
      default:
        return 'finger-print-outline';
    }
  }

  private async initializeEnabled(): Promise<void> {
    const value = await this.localStorage.read<boolean>(ENABLED_KEY);
    this.enabled.set(value === true);
  }

  private async initializeDeclined(): Promise<void> {
    const value = await this.localStorage.read<boolean>(DECLINED_KEY);
    this.declined.set(value === true);
  }

  private async initializePromptCount(): Promise<void> {
    const value = await this.localStorage.read<number>(PROMPT_COUNT_KEY);
    this.promptCount.set(typeof value === 'number' ? value : 0);
  }

  private async initializeBiometryType(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    try {
      const result = await NativeBiometric.isAvailable({ useFallback: false });
      this.biometryType.set(result.biometryType);
    } catch {
      // Keep default BiometryType.NONE
    }
  }
}
